import type { CameraIdRow } from "@/components/briefing/CameraIdCard";

// "Copy from previous day" for the Camera ID card.
//
// On a multi-day production a camera operator usually keeps the same
// Camera ID every day, so re-typing it per day is busywork. This mirrors
// the existing "Copy from Previous Day" affordance on the stages step.
//
// Rows are keyed `${personKey}__${eventDate}` by CreateEditBriefing, so
// the same person is matched across days by stripping the date suffix —
// NOT by name (two people can share a name; a renamed person must not
// inherit a stranger's ID).

/** Derive the per-person identity from a (person, day) row key. */
function personKeyOf(row: Pick<CameraIdRow, "key" | "eventDate">): string {
  const suffix = `__${row.eventDate}`;
  return row.key.endsWith(suffix) ? row.key.slice(0, -suffix.length) : row.key;
}

/**
 * The day immediately before `targetDay` in the sorted list of days
 * that actually have camera/photo crew, or null when `targetDay` is the
 * first such day. Drives whether the copy button is shown.
 */
export function previousCameraDay(days: string[], targetDay: string): string | null {
  const idx = days.indexOf(targetDay);
  return idx > 0 ? days[idx - 1] : null;
}

/**
 * Compute the Camera ID changes to copy `fromDay`'s assignments onto
 * `targetDay`: each person working `targetDay` who also worked `fromDay`
 * inherits their `fromDay` Camera ID. Returns only rows whose value
 * actually changes, as `{ key, cameraId }` pairs ready for onChange.
 *
 * People absent on `fromDay` are left untouched — copying must never
 * blank out an ID the producer already set on the target day.
 */
export function copyCameraIdsFromPreviousDay(
  rows: CameraIdRow[],
  targetDay: string,
  fromDay: string,
): { key: string; cameraId: string }[] {
  const prevByPerson = new Map<string, string>();
  for (const r of rows) {
    if (r.eventDate === fromDay) prevByPerson.set(personKeyOf(r), r.cameraId);
  }

  const changes: { key: string; cameraId: string }[] = [];
  for (const r of rows) {
    if (r.eventDate !== targetDay) continue;
    const prev = prevByPerson.get(personKeyOf(r));
    if (prev !== undefined && prev !== r.cameraId) {
      changes.push({ key: r.key, cameraId: prev });
    }
  }
  return changes;
}
